import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { StringUtilsHelper } from 'src/app/utils/string-utils-helper';
import { BiStgPlayerBiVizDimsDatasource } from '../../common/sql-maps/bicore-player-dims-sql-map';
import { BiVizDashboardCampaignsDatasource } from '../../mopa-campaigns/sql-maps/biviz-dashboard-campaigns-sql-map';
import { BIVizDashboardSales } from '../../mopa-sales/sql-maps/biviz-dashboard-sales-datasource-sql-map';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface CampaignsMetricsByDateDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId;
    queryCacheService: QueryCacheService;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class CampaignsMetricsByDateDataLoaderFactory extends Factory<
    DataLoader,
    CampaignsMetricsByDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'CampaignsMetricsByDateDataLoaderFactory';

    getInstance(options: CampaignsMetricsByDateDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                const campaignMastertableDatasource =
                    BiVizDashboardCampaignsDatasource;

                const campaignsMastertableSqlMap =
                    campaignMastertableDatasource.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    campaignsMastertableSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                // TODO add context settings. Remember to update chart options build logic in order to support dynamic choice of day_no
                const activeAll =
                    queryContext.settings[
                        QueryContextSettingID.CAMPAIGN_ACTIVE_ALL
                    ] ?? 'all';
                const activeCampaignSQL: Partial<ICompiledSQLTokens> =
                    activeAll === 'all'
                        ? {}
                        : {
                              where: [`active_campaign IS TRUE`],
                          };

                const campaignsMastertableSql =
                    campaignMastertableDatasource.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        options,
                        mergeSqlTokensArray([activeCampaignSQL])
                    );

                const salesDatasource = BIVizDashboardSales;

                // identify columns not supported in datasource
                const salesDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    salesDatasource.sqlMap.columns
                );

                const salesDsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    salesDsColumns.missing
                );

                // ignore columns that are not supported in this query
                // const ignoredContextColumnIds = [
                //     'ACTIVITY_DATE',
                //     'ACTIVITY_APP_VERSION',
                //     'ACTIVITY_APP_VERSION_GROUP_MINOR',
                // ];

                //filter out ignored columns from dsColumns.present
                // salesDsColumns.present = salesDsColumns.present.filter(
                //     (col) => !ignoredContextColumnIds.includes(col.id)
                // );

                const salesSQL =
                    salesDatasource.sqlStatementsCompiler.compileSQLStatements(
                        salesDsColumns.present,
                        queryContext.settings,
                        options
                        // selectedDayNoSQL
                    );

                const usersdashActivity = BiVizDashboardUsersActivity;

                const usersDsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    usersdashActivity.sqlMap.columns
                );
                const usersDsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    usersDsColumns.missing
                );
                const usersSQL = usersdashActivity.compileSQLStatements(
                    usersDsColumns.present,
                    queryContext.settings,
                    options
                );

                const playerBiVizDims = BiStgPlayerBiVizDimsDatasource;
                // identify columns not supported in datasource
                const playerDims = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    playerBiVizDims.sqlMap.columns
                );

                const playerDimsColumnsStatus =
                    DataLoader.getMissingColumnsStatus(playerDims.missing);

                const playerDimsSQL = playerBiVizDims.compileSQLStatements(
                    playerDims.present,
                    queryContext.settings,
                    options,
                    { where: ['player_dim.validation.unified_valid'] }
                );

                const windowRunning = `PARTITION BY ${StringUtilsHelper.suffixIfNotNull(
                    playerDimsSQL.splitGroup,
                    ', '
                )}date ORDER BY day_no`;

                const revenueType = (() => {
                    let setting =
                        queryContext.settings[
                            QueryContextSettingID.REVENUE_TYPE
                        ] ?? 'total';
                    if (setting === 'inapp') {
                        return 'inapp_';
                    } else if (setting === 'ad') {
                        return 'ad_';
                    } else if (setting === 'total') {
                        return '';
                    } else {
                        throw new Error(
                            `Unknown revenue type setting: ${setting}`
                        );
                    }
                })();
                // pln/usd
                const currency =
                    queryContext.settings[QueryContextSettingID.CURRENCY] ??
                    'pln';
                const settingSetGross =
                    queryContext.settings[QueryContextSettingID.NET_GROSS] ??
                    'net';
                const netGross = settingSetGross === 'net' ? 'net_' : 'gross_';

                /**
                 * Generate join condition SQL
                 * @param aliasA
                 * @param aliasB
                 * @returns Example `ON dsA.COLUMN_ID = dsB.COLUMN_ID`
                 */
                // const finalDataJoinConditionSql = (options: {
                //     aliasA: string;
                //     aliasB: string;
                //     conditions?: string[];
                // }) => {
                //     const splitDatasourceColumnIds =
                //         queryContext.datasourceColumns
                //             .filter((col) => col.state.isSplitApplied)
                //             .map(
                //                 (col) =>
                //                     `${options.aliasA}.${col.id} = ${options.aliasB}.${col.id}`
                //             );
                //     // preadd join condition if there are any conditions
                //     if (options.conditions && options.conditions.length > 0) {
                //         splitDatasourceColumnIds.unshift(...options.conditions);
                //     }
                //     // join them with AND
                //     return splitDatasourceColumnIds.length > 0
                //         ? `ON ${splitDatasourceColumnIds.join(' AND ')}`
                //         : '';
                // };
                const revenueSql = `${revenueType}revenue_${netGross}${currency}`;
                const costsSql = `cost_${currency}`;
                // TODO use queryContext setting to get all days
                const metricDays = queryContext.settings[
                    QueryContextSettingID.ROAS_SELECTED_DAY_NO
                ] ?? ['1', '3', '7'];

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH running_metrics AS
(
  WITH revenue AS
(
  SELECT ${playerDimsSQL.splitGroupBy}
    date
    , day_no
    , SUM(${revenueSql}) AS ${revenueSql}
    , SUM(transactions) AS transactions
    , SUM(total_${revenueSql}) AS total_${revenueSql}
  FROM
  (
    SELECT *
      , SUM(${revenueSql}) OVER (
        PARTITION BY ${playerDimsSQL.splitGroupBy}force_daily_cohort
        ORDER BY day_no
      ) AS total_${revenueSql}
    FROM 
    (
      SELECT
        ${salesSQL.splitSelect}
        ${salesSQL.dateTrunc(
            salesDatasource.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL
        )} AS date
        , ${salesDatasource.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE?.columnSQL} AS force_daily_cohort
        , day_no
        , SUM(${revenueSql}) AS ${revenueSql}
        , SUM(transactions) AS transactions
      FROM \`${generateTableFullPath(
          options.gcpProjectId,
          salesDatasource.sqlMap.dataset,
          salesDatasource.sqlMap.tableId
      )}\` ds
      ${salesSQL.join}
      ${salesSQL.where}
      GROUP BY ALL
    )
  )
  GROUP BY ALL
)
    
  , campaign_cost AS
  (  
    SELECT
      ${campaignsMastertableSql.splitGroupBy}
      ${campaignsMastertableSql.dateTrunc()} AS date,
      day_no,
      SUM(costs) AS costs
    FROM
    (
      SELECT
        ${campaignsMastertableSql.splitSelect}
        ${
            campaignMastertableDatasource.sqlMap.columns
                .FIREBASE_FIRST_OPEN_DATE?.columnSQL
        } AS date ,
        SUM(${costsSql}) AS costs,
        ( SELECT all_days FROM \`${options.gcpProjectId}.bi_stg.cohort_days\` cd WHERE cd.first_session_date = ds.first_session_date ) AS all_days
          FROM \`${generateTableFullPath(
              options.gcpProjectId,
              campaignMastertableDatasource.sqlMap.dataset,
              campaignMastertableDatasource.sqlMap.tableId
          )}\` ds
      ${campaignsMastertableSql.where}
      GROUP BY ALL
    ), UNNEST(all_days) AS day_no 
    GROUP BY ALL
  )
  
  , by_fod AS
  (
    SELECT
      ${playerDimsSQL.splitGroupBy}
      date,
      day_no,
      
      -- Legacy logic kept for reference only.
      -- e_ROAS = running sum of daily ratios.
      -- Current logic uses only those cohorts that have matured to dN.
      -- ROUND(SUM(SAFE_DIVIDE(revenue, costs)) OVER (${windowRunning}), 4) AS e_roas
      
      -- Current logic:
      -- Total revenue up to the given day / Only mature costs that reached that life day
      ROUND(SAFE_DIVIDE(COALESCE(r.total_${revenueSql}, 0), costs), 4) AS roas

    FROM revenue r
    LEFT JOIN campaign_cost campaigns
      USING(${StringUtilsHelper.suffixIfNotNull(
          playerDimsSQL.splitGroup,
          ', '
      )}date, day_no)
  )

  SELECT *
  FROM by_fod
  PIVOT( MAX(roas) AS roas FOR day_no IN (${metricDays
      .map((d) => `${d} as d${d}`)
      .join(', ')}) )
)

, mastertable_base AS
(
  SELECT
    ${campaignsMastertableSql.splitSelect}
    ${campaignsMastertableSql.dateTrunc(
        campaignMastertableDatasource.sqlMap.columns.FIREBASE_FIRST_OPEN_DATE
            ?.columnSQL
    )} AS date,
    -- source: AppsFlyer
    ROUND(SUM(cost_${currency}), 2) AS costs
    -- source: BIEvents
    , ROUND(SUM(${revenueSql}), 2) AS income
    -- source: BIEvents + reattribution from BIEvents level campaign info
    --, ROUND(SUM(reattr_${revenueSql}), 2) AS reattribution_income
    -- source: revenue from BIEvents, cost from AppsFlyer
    --, ROUND(SAFE_DIVIDE(SUM(${revenueSql}), SUM(cost_${currency})), 4) AS total_roas
    -- source: BIEvents
    , ROUND(SAFE_DIVIDE(SUM(ad_revenue_${netGross}${currency}), SUM(revenue_${netGross}${currency})), 4) AS ad_revenue_share
    -- source: AppsFlyer
    , SUM(impressions) AS impressions
    -- source: AppsFlyer
    , SUM(installs) AS installs
    -- source: BIEvents
    , SUM(transaction_count) AS transactions
    -- source: BIEvents
    , SUM(payers) AS payers
    -- source: BIEvents
    , SUM(payers_d7) AS payers_d7
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(installs)), 2) AS cpi
    -- cost: - AppsFlyer; transactions: BIEvents
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(transaction_count)), 2) AS cpt
    , ROUND(1000 * SAFE_DIVIDE(SUM(cost_${currency}), SUM(impressions)), 2) AS cpm
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(payers)), 2) AS cppu
    , ROUND(SAFE_DIVIDE(SUM(cost_${currency}), SUM(payers_d7)), 2) AS cppu_d7
    , ROUND(1000 * SAFE_DIVIDE(SUM(installs), SUM(impressions)), 2) AS ipm
    --  source: AppsFlyer
    , ROUND(SUM(reattributions), 2) AS reattributions    
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      campaignsMastertableSqlMap.dataset,
      campaignsMastertableSqlMap.tableId
  )}\`
  ${campaignsMastertableSql.where}
  GROUP BY ALL
)

SELECT m.* 
  ${metricDays.map((d) => `, runnings.roas_d${d}`).join('\n')}
FROM mastertable_base m
LEFT JOIN running_metrics runnings
  ON m.date = runnings.date
  ${
      campaignsMastertableSql.splitGroup
          ? `AND m.${campaignsMastertableSql.splitGroup} = runnings.${campaignsMastertableSql.splitGroup}`
          : ''
  }
ORDER BY ${campaignsMastertableSql.splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);
                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    queryContext.settings[
                        QueryContextSettingID.DATE_AGGREGATION
                    ] ?? 'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}

export class CampaignsMastertableDocumentation {
    static campaignsTable = /* HTML */ ` <h2>Sources of metrics</h2>
        <pre>
- costs: AppsFlyer
- income: BIEvents
- reattribution_income: BIEvents + reattribution from BIEvents level campaign info 
- total_roas: revenue: BIEvents, cost: AppsFlyer 
- ad_revenue_share: revenue: BIEvents 
- impressions: AppsFlyer 
- installs: AppsFlyer
- transactions: BIEvents 
- payers: BIEvents 
- payers_d7: BIEvents
- cpi: AppsFlyer 
- cpt: cost: - AppsFlyer; transactions: BIEvents
- cmp: AppsFlyer 
- cppu: cost: AppsFlyer; payers: BIEvents 
- cppu_d7: cost: AppsFlyer; payers_d7: BIEvents 
- ipm: AppsFlyer 
- roas_dN: revenue: BIEvents, cost: AppsFlyer 
- arpu_dN: revenue: BIEvents, present_eq_d0: BIEvents 
UWAGA! arpu_dN nie uwzględnia installs z AppsFlyer tylko liczbę użytkowników w d0 z BIEvents (aby zachować spójność z arpu na pozostałych dashboardach) 
- cpt_dN: cost: AppsFlyer; transactions: BIEvents
        </pre
        >`;
}
